import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormLabel from "components/Extra/FormLabel"
import Select from "react-select"
import { productsData } from "pages/admin/Products"
import { FormInput } from "components/Extra/FormInput"
import { FormImageInput } from "components/Extra/FormImageInput"
import { useTranslation } from "react-i18next"

interface AddNewCampaignProps {
  isOpen: boolean
  toggle: () => void
}
interface SelectOption {
  id: string
  name: string
}

const campaignTypes: string[] = ["Product", "Category", "Featured", "Banner"]

export const AddNewCampaign: React.FC<AddNewCampaignProps> = ({
  isOpen,
  toggle,
}) => {
  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("0")
  const [openThirdAccordion, setOpenThirdAccordion] = useState("0")
  const [openFourthAccordion, setOpenFourthAccordion] = useState("0")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const paymentMethods: { id: string; name: string }[] = useMemo(() => {
    return [
      { id: "cash", name: t("campaigns_cash") },
      { id: "online", name: t("campaigns_online") },
    ]
  }, [t])

  const customerSegmentOptions: { id: string; name: string }[] = useMemo(() => {
    return [
      { id: "all_nartawi", name: t("campaigns_allNartawiCustomers") },
      { id: "current_only", name: t("campaigns_currentCustomersOnly") },
      { id: "current_previous", name: t("campaigns_currentAndPreviousCustomers") },
    ]
  }, [t])

  const couponBasedOptions: { id: string; name: string }[] = useMemo(() => {
    return [
      { id: "one_time", name: t("campaigns_oneTimePurchase") },
      { id: "coupon_based", name: t("campaigns_basedOnCoupon") },
    ]
  }, [t])

  const purchaseRateOptions: { id: string; name: string }[] = useMemo(() => {
    return [
      { id: "100-1000", name: "100 - 1000" },
      { id: "1001-5000", name: "1001 - 5000" },
      { id: "5001-10000", name: "5001 - 10000" },
      { id: "10001+", name: "10001+" },
    ]
  }, [])

  const regionOptions: { id: string; name: string }[] = useMemo(() => {
    return [
      { id: "doha", name: t("campaigns_doha") },
      { id: "al_rayyan", name: t("campaigns_alRayyan") },
      { id: "al_wakrah", name: t("campaigns_alWakrah") },
      { id: "al_khor", name: t("campaigns_alKhor") },
      { id: "umm_salal", name: t("campaigns_ummSalal") },
      { id: "al_daayen", name: t("campaigns_alDaayen") },
      { id: "al_shamal", name: t("campaigns_alShamal") },
    ]
  }, [t])

  const campaignTitleOptions: { id: string; name: string }[] = useMemo(() => {
    return [
      { id: "banner", name: "Banner" },
      { id: "featured_stored", name: "Featured Stored" },
      { id: "featured", name: "Featured" },
      { id: "product", name: "Product" },
      { id: "push_notification", name: "Push notification" },
    ]
  }, [])

  const [selectedProducts, setselectedProducts] = useState<
    | {
        id: string
        name: string
      }[]
    | []
  >([])
  const [selectedCampaignTitle, setSelectedCampaignTitle] = useState<{
    id: string
    name: string
  } | null>(null)
  const [selectedCampaignType, setselectedCampaignType] = useState<{
    id: string
    name: string
  } | null>(null)
  const [selectedPaymentMethod, setselectedPaymentMethod] = useState<{
    id: string
    name: string
  } | null>(null)
  const [selectedCustomerSegment, setSelectedCustomerSegment] = useState<{
    id: string
    name: string
  } | null>(null)
  const [selectedCouponBased, setSelectedCouponBased] = useState<{
    id: string
    name: string
  } | null>(null)
  const [selectedPurchaseRate, setSelectedPurchaseRate] = useState<{
    id: string
    name: string
  } | null>(null)
  const [selectedRegion, setSelectedRegion] = useState<{
    id: string
    name: string
  } | null>(null)

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      campaignTitle: null as { id: string; name: string } | null,
      products: [] as { id: string; name: string }[],
      campaignType: null as { id: string; name: string } | null,
      campaignImage: null as {
        id: string
        name: string
        file?: File
        preview?: string
      } | null,
      startDate: "",
      endDate: "",
      timeFrom: "",
      timeTo: "",
      price: 100, // FIXME: it change depend on the details of campaign
      paymentMethod: null as { id: string; name: string } | null,
      customerSegment: null as { id: string; name: string } | null,
      couponBased: null as { id: string; name: string } | null,
      purchaseRate: null as { id: string; name: string } | null,
      region: null as { id: string; name: string } | null,
    },
    validationSchema: Yup.object({
      campaignTitle: Yup.object({
        id: Yup.string().required(
          t("campaigns_campaignTitleValidationMessage") as string
        ),
        name: Yup.string().required(
          t("campaigns_campaignTitleValidationMessage") as string
        ),
      }),
      products: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(
            t("campaigns_productIdValidationMessage") as string
          ),
          name: Yup.string().required(
            t("campaigns_productNameValidationMessage") as string
          ),
        })
      ),
      campaignType: Yup.object({
        id: Yup.string().required(
          t("campaigns_campaignTypeValidationMessage") as string
        ),
        name: Yup.string().required(
          t("campaigns_campaignTypeValidationMessage") as string
        ),
      }),
      //   campaignImage: Yup.object(),
      startDate: Yup.date()
        .required(t("campaigns_startDateValidationMessage") as string)
        .max(
          Yup.ref("endDate"),
          t("campaigns_maxStartDateValidationMessage") as string
        ),
      endDate: Yup.date()
        .required(t("campaigns_endDateValidationMessage") as string)
        .min(
          Yup.ref("startDate"),
          t("campaigns_minEndDateValidationMessage") as string
        ),
      timeFrom: Yup.string().required(
        t("campaigns_startTimeValidationMessage") as string
      ),
      timeTo: Yup.string().required(
        t("campaigns_endTimeValidationMessage") as string
      ),
      price: Yup.number()
        .min(1, t("campaigns_minPriceValidationMessage") as string)
        .required(t("campaigns_priceValidationMessage") as string),
      paymentMethod: Yup.object({
        id: Yup.string().required(
          t("campaigns_paymentValidationMessage") as string
        ),
        name: Yup.string().required(
          t("campaigns_paymentValidationMessage") as string
        ),
      }),
    }),
    onSubmit: values => {
      const {
        campaignImage,
        campaignTitle,
        campaignType,
        endDate,
        paymentMethod,
        price,
        products,
        startDate,
        timeFrom,
        timeTo,
      } = values
      console.log(values)

      // TODO: Dispatch
      validation.resetForm()
      toggle()
    },
  })

  useEffect(() => {
    validation.resetForm()
    setSelectedCampaignTitle(null)
  }, [isOpen])

  const selectProductHandler = useCallback(
    (data: { id: string; name: string }[]) => {
      setselectedProducts(data)
      validation.setFieldValue("products", data)
    },
    []
  )

  const addNewProductHandler = useCallback(() => {
    console.log("addNewProductHandler")
  }, [])

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return

    const file = e.target.files[0]
    const singleImage = {
      id: `${Date.now()}`,
      name: file.name,
      file,
      preview: URL.createObjectURL(file),
    }

    validation.setFieldValue("campaignImage", singleImage)
  }

  const handleRemove = useCallback(() => {
    validation.setFieldValue("campaignImage", null)
  }, [])

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h3">
        <h4 className="text-capitalize fw-semibold font-size-24">
          {t("campaigns_assignNewCampaign")}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Accordion open={openFirstAccordion}>
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
                }
              >
                {t("common_basicInfo")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormGroup>
                  <FormLabel htmlFor="campaignTitle" required>
                    {t("campaigns_campaignTitle")}
                  </FormLabel>
                  <Select
                    id="campaignTitle"
                    value={selectedCampaignTitle}
                    onChange={(selectedOption: {
                      id: string
                      name: string
                    }) => {
                      setSelectedCampaignTitle(selectedOption)
                      validation.setFieldValue("campaignTitle", {
                        id: selectedOption?.id,
                        name: selectedOption?.name,
                      })
                    }}
                    options={campaignTitleOptions}
                    getOptionLabel={(option: { id: string; name: string }) =>
                      option.name
                    }
                    getOptionValue={(option: { id: string; name: string }) =>
                      option.id
                    }
                    className="select2-selection"
                  />
                  {validation.touched.campaignTitle &&
                  validation.errors.campaignTitle ? (
                    <FormFeedback type="invalid">
                      {validation.errors.campaignTitle}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <div className="d-flex align-items-end justify-content-start">
                    <div className="flex-grow-1 me-2">
                      <FormLabel htmlFor="products" required>
                        {t("campaigns_products")}
                      </FormLabel>
                      <Select
                        id="products"
                        value={selectedProducts}
                        isMulti
                        onChange={(
                          selectedOption: { id: string; name: string }[] | []
                        ) =>
                          selectProductHandler(
                            selectedOption as { id: string; name: string }[]
                          )
                        }
                        options={productsData.map(one => ({
                          id: one.id,
                          name: one.name,
                        }))}
                        getOptionLabel={(option: SelectOption) => option.name}
                        getOptionValue={(option: SelectOption) => option.id}
                        className="select2-selection"
                      />
                    </div>
                    <span
                      className="input-group-append"
                      style={{ cursor: "pointer" }}
                      onClick={addNewProductHandler}
                    >
                      <i className="bx bx-plus font-size-18 p-2 px-2 input-group-text"></i>
                    </span>
                  </div>
                  {validation.touched.paymentMethod &&
                  validation.errors.paymentMethod ? (
                    <FormFeedback type="invalid">
                      {validation.errors.paymentMethod}
                    </FormFeedback>
                  ) : null}
                  <div className="d-flex align-items-center gap-1 ms-2">
                    <i className="bx bx-info-circle"></i>
                    <FormFeedback
                      type="invalid"
                      className="d-block text-secondary"
                    >
                      {t("campaigns_multipleProduct")}
                    </FormFeedback>
                  </div>
                </FormGroup>
                <FormImageInput
                  label={t("campaigns_campaignImage") as string}
                  changeImageHandler={handleFilesChange}
                  name="campaignImage"
                  validation={validation}
                  removeHandler={handleRemove}
                />
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openSecondAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenSecondAccordion(
                    openSecondAccordion === "0" ? "1" : "0"
                  )
                }
              >
                {t("campaigns_schedule")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("common_startDate") as string}
                      name="startDate"
                      type="date"
                      validation={validation}
                      required
                    />
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("common_endDate") as string}
                      name="endDate"
                      type="date"
                      validation={validation}
                      required
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("common_periodTimeFrom") as string}
                      name="timeFrom"
                      type="time"
                      validation={validation}
                      required
                    />
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("common_periodTimeTo") as string}
                      name="timeTo"
                      type="time"
                      validation={validation}
                      required
                    />
                  </Col>
                </Row>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openThirdAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenThirdAccordion(openThirdAccordion === "0" ? "1" : "0")
                }
              >
                {t("campaigns_targetAudience")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <FormLabel htmlFor="customerSegment">
                        {t("campaigns_selectCustomers")}
                      </FormLabel>
                      <Select
                        id="customerSegment"
                        value={selectedCustomerSegment}
                        onChange={(selectedOption: {
                          id: string
                          name: string
                        }) => {
                          setSelectedCustomerSegment(selectedOption)
                          validation.setFieldValue("customerSegment", {
                            id: selectedOption?.id,
                            name: selectedOption?.name,
                          })
                        }}
                        options={customerSegmentOptions}
                        getOptionLabel={(option: { id: string; name: string }) =>
                          option.name
                        }
                        getOptionValue={(option: { id: string; name: string }) =>
                          option.id
                        }
                        className="select2-selection"
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <FormLabel htmlFor="couponBased">
                        {t("campaigns_couponBased")}
                      </FormLabel>
                      <Select
                        id="couponBased"
                        value={selectedCouponBased}
                        onChange={(selectedOption: {
                          id: string
                          name: string
                        }) => {
                          setSelectedCouponBased(selectedOption)
                          validation.setFieldValue("couponBased", {
                            id: selectedOption?.id,
                            name: selectedOption?.name,
                          })
                        }}
                        options={couponBasedOptions}
                        getOptionLabel={(option: { id: string; name: string }) =>
                          option.name
                        }
                        getOptionValue={(option: { id: string; name: string }) =>
                          option.id
                        }
                        className="select2-selection"
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <FormLabel htmlFor="purchaseRate">
                        {t("campaigns_purchaseRate")}
                      </FormLabel>
                      <Select
                        id="purchaseRate"
                        value={selectedPurchaseRate}
                        onChange={(selectedOption: {
                          id: string
                          name: string
                        }) => {
                          setSelectedPurchaseRate(selectedOption)
                          validation.setFieldValue("purchaseRate", {
                            id: selectedOption?.id,
                            name: selectedOption?.name,
                          })
                        }}
                        options={purchaseRateOptions}
                        getOptionLabel={(option: { id: string; name: string }) =>
                          option.name
                        }
                        getOptionValue={(option: { id: string; name: string }) =>
                          option.id
                        }
                        className="select2-selection"
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <FormLabel htmlFor="region">
                        {t("campaigns_byRegion")}
                      </FormLabel>
                      <Select
                        id="region"
                        value={selectedRegion}
                        onChange={(selectedOption: {
                          id: string
                          name: string
                        }) => {
                          setSelectedRegion(selectedOption)
                          validation.setFieldValue("region", {
                            id: selectedOption?.id,
                            name: selectedOption?.name,
                          })
                        }}
                        options={regionOptions}
                        getOptionLabel={(option: { id: string; name: string }) =>
                          option.name
                        }
                        getOptionValue={(option: { id: string; name: string }) =>
                          option.id
                        }
                        className="select2-selection"
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openFourthAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFourthAccordion(openFourthAccordion === "0" ? "1" : "0")
                }
              >
                {t("campaigns_price")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormInput
                  label={t("common_priceCurrency") as string}
                  name="price"
                  type="number"
                  placeholder={t("campaigns_enterPrice") as string}
                  validation={validation}
                  info={t("campaigns_priceInfo") as string}
                  required
                  readOnly
                />

                <FormGroup>
                  <FormLabel htmlFor="paymentMethod" required>
                    {t("campaigns_payment")}
                  </FormLabel>
                  <Select
                    id="paymentMethod"
                    value={selectedPaymentMethod}
                    onChange={(selectedOption: {
                      id: string
                      name: string
                    }) => {
                      setselectedPaymentMethod(selectedOption)
                      validation.setFieldValue("paymentMethod", {
                        id: selectedOption?.id,
                        name: selectedOption?.name,
                      })
                    }}
                    options={paymentMethods}
                    getOptionLabel={(option: { id: string; name: string }) =>
                      option.name
                    }
                    getOptionValue={(option: { id: string; name: string }) =>
                      option.id
                    }
                    className="select2-selection"
                  />
                  {validation.touched.paymentMethod &&
                  validation.errors.paymentMethod ? (
                    <FormFeedback type="invalid">
                      {validation.errors.paymentMethod}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn btn-primary save-customer`}
                  disabled={!(validation.isValid && validation.dirty)}
                >
                  {t("campaigns_addCampaign")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
